import React from 'react';
import { Box, Typography } from '@mui/material';
import PanelBlock, { PanelEyebrow } from '../PanelBlock';
import DatapointRows from './DatapointRows';
import SectionAssessment from './SectionAssessment';
import { LABOKLIN_RED } from '../../../theme/laboklinTheme';
import {
  Assessment,
  AssessmentSection,
  ReportScore,
  RunPanels,
  SectionStability,
  SECTION_LABELS,
  gradeOf,
  RunStep,
} from '../../../types/labConsistency';

/**
 * One graded section: its numbers, its datapoints, and the veterinarian's verdict.
 *
 * Every number rendered here is the STORED one. The page never recomputes a metric the
 * server also computes - that is what stops this view and the markdown scorecard
 * disagreeing about the same run. It derives only presence from the run items it
 * already holds, which is membership rather than a measure.
 */

const GRADE_COLOUR = {
  good: 'rgb(30, 130, 60)',
  warn: 'rgb(190, 130, 20)',
  bad: LABOKLIN_RED,
} as const;

interface ConsistencySectionProps {
  section: AssessmentSection;
  stability: SectionStability;
  /** The same measure over the engine's own output, where the section has one. */
  engine?: SectionStability;
  runs: RunPanels[];
  report: ReportScore;
  step: RunStep;
  assessment?: Assessment;
  proseFor: (run: RunPanels, itemId: number) => string | null;
  rankFor?: (run: RunPanels, itemId: number) => number | null;
  emptyMessage: string;
}

const Metric: React.FC<{ label: string; value: number; metric: 'presence' | 'content' }> = ({
  label,
  value,
  metric,
}) => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline' }}>
    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: GRADE_COLOUR[gradeOf(value, metric)] }}>
      {value.toFixed(2)}
    </Typography>
  </Box>
);

export const ConsistencySection: React.FC<ConsistencySectionProps> = ({
  section,
  stability,
  engine,
  runs,
  report,
  step,
  assessment,
  proseFor,
  rankFor,
  emptyMessage,
}) => {
  const total = stability.runs;
  const settled = stability.items.filter((i) => i.runs === total).length;

  return (
    <PanelBlock>
      <PanelEyebrow label={SECTION_LABELS[section]} state={`${total} runs`} />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 0.75 }}>
        <Metric label="presence" value={stability.presenceAgreement} metric="presence" />
        <Metric label="content" value={stability.contentAgreement} metric="content" />
        {section === 'differentials' && (
          <Metric label="lead" value={stability.leadAgreement} metric="content" />
        )}
        {/*
          The engine's own number beside the panel's, because that pairing is what turns
          "the answer varies" into something fixable. On one report the engine returned
          an identical ranking on every run while the exclusions came back 0, 1, 2, 0 -
          read alone the differential just looked unstable; read beside the engine it
          pointed straight at the review.
        */}
        {engine && (
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            engine {engine.contentAgreement.toFixed(2)}
          </Typography>
        )}
      </Box>

      {/*
        The plain-language companion. Jaccard is otherwise read as "how many runs
        agreed", which it is not: a ten-item list differing by one already scores ~0.90
        while a three-item list missing one scores 0.67. The number has to be
        checkable by eye or it is not evidence.
      */}
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
        {stability.items.length === 0
          ? emptyMessage
          : `${settled} of ${stability.items.length} appeared in all ${total} runs`}
      </Typography>

      <DatapointRows
        items={stability.items}
        runs={runs}
        proseFor={proseFor}
        rankFor={rankFor}
        emptyMessage={emptyMessage}
      />

      <SectionAssessment
        batchId={report.batchId}
        reportNo={report.reportNo}
        step={step}
        section={section}
        contentHash={report.contentHash}
        existing={assessment}
      />
    </PanelBlock>
  );
};

export default ConsistencySection;
